/**
 * firestoreCore.js
 * Purpose: Shared, low-level Firestore helpers (no business logic).
 * Exposes: DEFAULT_CAP, getDbOrThrow, normalizeEmail, toQueryDate, mapRawDocs,
 *          assertCollectionName, col, subscribeByEmail.
 */

import { collection, onSnapshot, query, where, limit, Timestamp } from "firebase/firestore";
// ⬇️ point to firebaseApp (not firebaseController)
import { getFirebase } from "./firebaseApp";

export const DEFAULT_CAP = 500;

export function getDbOrThrow() {
  const { db, ready, reason } = getFirebase();
  if (!ready || !db) throw new Error(`Firebase not ready: ${reason || "no db"}`);
  return db;
}

export function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}

/**
 * Convert input to Firestore-comparable query values:
 * - "YYYY-MM-DD" string → keep as string
 * - JS Date → Timestamp.fromDate(date)
 * - Firestore Timestamp → as-is
 */
export function toQueryDate(v) {
  if (v == null || v === "") return undefined;
  if (typeof v === "string") return v;
  if (v instanceof Date) return Timestamp.fromDate(v);
  if (typeof v === "object" && typeof v.seconds === "number") return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : Timestamp.fromDate(d);
}

/** Map a QuerySnapshot to Firestore document data with id attached */
export function mapRawDocs(snap) {
  return snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
}

/** Assert a collection name string */
export function assertCollectionName(colName, label = "collection") {
  if (!colName || typeof colName !== "string") throw new Error(`Invalid ${label} name`);
  return colName;
}

/** Convenience: get a CollectionReference from a name */
export function col(db, name) {
  return collection(db, name);
}

/** Live subscribe by userEmail for any flat collection (returns mapped docs) */
export function subscribeByEmail({
  colRef,
  userEmail,
  cap = DEFAULT_CAP,
  extra = [],
  onData,
  onError,
}) {
  const email = normalizeEmail(userEmail);
  if (!colRef) throw new Error("subscribeByEmail: invalid collection ref");
  if (!email) throw new Error("subscribeByEmail: missing userEmail");
  if (typeof onData !== "function") throw new Error("subscribeByEmail: onData must be a function");

  const qy = query(colRef, where("userEmail", "==", email), ...(extra || []), limit(cap));
  return onSnapshot(
    qy,
    (snap) => onData(mapRawDocs(snap)),
    (err) => onError?.(err)
  );
}
